/**
 * FiasOwnedPluginsList — every plugin the Fias platform account owns, each with
 * a "Manage team" disclosure that opens the PluginTeamPanel.
 *
 * Same rows as the contributor dashboard's admin section (which is kept for
 * the self-service "Become maintainer" path that ADR-019 linking needs); this
 * one exists to staff OTHER people. A freshly annexed plugin is opened
 * automatically so the next step is in view.
 */

import * as React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePlatformOwnedPlugins } from '@/contributor/hooks/usePlatformOwnedPlugins';
import {
  ProvenanceLine,
  WorkspaceLinkSummary,
} from '@/contributor/components/PlatformOwnedPluginsSection';
import { PluginTeamPanel } from './PluginTeamPanel';

const PAGE_SIZE = 25;

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'unknown error';
}

interface FiasOwnedPluginsListProps {
  /** Arche to open the team panel for on arrival (e.g. one just annexed). */
  autoOpenArcheId: string | null;
}

export function FiasOwnedPluginsList({ autoOpenArcheId }: FiasOwnedPluginsListProps) {
  const [offset, setOffset] = React.useState(0);
  const [openArcheId, setOpenArcheId] = React.useState<string | null>(null);
  const query = usePlatformOwnedPlugins({ limit: PAGE_SIZE, offset });

  React.useEffect(() => {
    if (autoOpenArcheId) {
      setOffset(0);
      setOpenArcheId(autoOpenArcheId);
    }
  }, [autoOpenArcheId]);

  const arches = query.data?.arches ?? [];
  const total = query.data?.total ?? 0;
  // The listing's order is the server's, so a just-annexed plugin is not
  // guaranteed to be on the first page. Say so rather than silently opening
  // nothing.
  const autoOpenOffPage =
    !!autoOpenArcheId &&
    query.isSuccess &&
    offset === 0 &&
    !arches.some((a) => a.archeId === autoOpenArcheId);
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = offset + arches.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fias-owned plugins</CardTitle>
        <CardDescription>
          Annexed from a developer, or built first-party. Open a plugin&apos;s team to set who can
          read or publish it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {autoOpenOffPage && (
          <p className="text-sm text-muted-foreground" data-testid="auto-open-off-page">
            The plugin you just annexed is not on this page — use Next to find it and open its team.
          </p>
        )}
        {query.isLoading ? (
          <div className="space-y-2" data-testid="fias-plugins-loading">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : query.isError ? (
          <p role="alert" className="text-sm text-destructive">
            Could not load Fias-owned plugins: {errorMessage(query.error)}
          </p>
        ) : arches.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No plugins are owned by the Fias platform account yet.
          </p>
        ) : (
          <ul className="divide-y rounded border">
            {arches.map((plugin) => {
              const open = openArcheId === plugin.archeId;
              return (
                <li key={plugin.archeId} className="space-y-3 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row">
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{plugin.name}</span>
                        <Badge variant="secondary">{plugin.status}</Badge>
                        {plugin.lifecycleState !== 'active' && (
                          <Badge variant="destructive">{plugin.lifecycleState}</Badge>
                        )}
                        <ProvenanceLine plugin={plugin} />
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {plugin.archeId}
                      </div>
                      <div>
                        <div className="text-xs font-medium text-muted-foreground">
                          Workspace libraries
                        </div>
                        <WorkspaceLinkSummary links={plugin.workspaceLinks} />
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-row gap-2 lg:flex-col lg:items-end">
                      <Button
                        variant={open ? 'default' : 'outline'}
                        size="sm"
                        aria-expanded={open}
                        onClick={() => setOpenArcheId(open ? null : plugin.archeId)}
                      >
                        {open ? (
                          <ChevronDown className="mr-1 h-4 w-4" aria-hidden="true" />
                        ) : (
                          <ChevronRight className="mr-1 h-4 w-4" aria-hidden="true" />
                        )}
                        Manage team
                      </Button>
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/contributor/arches/${plugin.archeId}`}>Manage</Link>
                      </Button>
                    </div>
                  </div>
                  {open && <PluginTeamPanel archeId={plugin.archeId} pluginName={plugin.name} />}
                </li>
              );
            })}
          </ul>
        )}

        {total > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {rangeStart}–{rangeEnd} of {total}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0 || query.isFetching}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={rangeEnd >= total || query.isFetching}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
